import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { AppleGlyph, GooglePlayGlyph } from "../components/BrandGlyphs";
import { EditorialPage, handwritten } from "../components/editorial";
import {
  FeedVisual,
  MapVisual,
  CreateVisual,
  CommunitiesVisual,
  YouVisual,
} from "../components/AppMocks";
import StoryPageHero from "../components/story/StoryPageHero";
import Reveal from "../components/story/Reveal";
import Doodle from "../components/story/Doodle";
import Highlighter from "../components/story/Highlighter";
import Navbar from "../components/Navbar";
import Button from "../components/Button";
import { APP_STORE_URL, PLAY_STORE_URL } from "../lib/appLinks";
import { trackStoreClick } from "../lib/storeClicks";
import { cn } from "../lib/utils";

const sections = [
  {
    id: "feed",
    n: "01",
    title: "The Home tab",
    kicker: "A feed of plans, not posts",
    Visual: FeedVisual,
    bg: "var(--color-paper-salmon)",
    body: "Two feeds, one toggle. Your communities on the left for plans from groups you've joined. Discover on the right for everything else: interest-matched, nearby, and the people you've previously looped with. The endpoint is showing up IRL, not posting a clip.",
    bullets: [
      "Your communities: recency-weighted",
      "Discover: composite ranker with velocity + interest",
      "Public engagement: likes, saves, who's looped in",
    ],
  },
  {
    id: "map",
    n: "02",
    title: "The Map tab",
    kicker: "A map of right-now",
    Visual: MapVisual,
    bg: "var(--color-paper-mint)",
    body: "Spatial view, on by default. Your location is blurred to a privacy zone unless you tell it otherwise. We never show your exact pin to anyone but you. Filter by time-of-day: next hour, next 4 hours, today, this weekend.",
    bullets: [
      "Privacy zones around your home and work by default",
      "Distance filter: 10-min walk, bike, transit",
      "Time-of-day filter: right now, today, weekend",
    ],
  },
  {
    id: "create",
    n: "03",
    title: "The Create tab",
    kicker: "Post one in seconds",
    Visual: CreateVisual,
    // Light peach, NOT literal coral: these panels host the app mockups,
    // and the mockups carry a coral handwritten caption, which on a coral
    // panel measured 1.0:1 and simply vanished.
    bg: "var(--color-paper-salmon)",
    body: "Going somewhere anyway? Post it. Pick a photo, drop a pin, say what time. Three taps. The right people loop in; the wrong people don't see it. No event-management hellscape.",
    bullets: [
      "Three-tap create: photo, pin, time",
      "Per-loop filters: age range, friends-of-friends, vouch required",
      "Auto-shared to your communities if you want",
    ],
  },
  {
    id: "communities",
    n: "04",
    title: "The Communities tab",
    kicker: "Real, recurring groups",
    Visual: CommunitiesVisual,
    bg: "var(--color-paper-tan)",
    body: "Run clubs, book clubs, supper crews. Communities are how recurring loops live in one place. You join a community once and never have to re-find your run crew on Sunday morning.",
    bullets: [
      "LetsLoop-seeded communities in every active area",
      "Anyone can start one, verified after 3 loops",
      "Auto-RSVP to your regulars (opt-in)",
    ],
  },
  {
    id: "you",
    n: "05",
    title: "The You tab",
    kicker: "People you've looped with",
    Visual: YouVisual,
    bg: "var(--color-paper-salmon)",
    body: "Your profile remembers who you've shown up with. Over time, this is the most valuable thing you have on the app, a graph of people who actually share your time, not just your contacts.",
    bullets: [
      "Looped-with grid: every person you've met IRL",
      "Hosting history: response rate, average attendance",
      "Identity-first, never anonymous",
    ],
  },
];

export default function HowItWorksPage() {
  useEffect(() => {
    document.title = "How it works | LetsLoop";
    // jump to hash anchor if present
    if (typeof window !== "undefined" && window.location.hash) {
      const id = window.location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        // tiny delay so layout settles
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, []);

  return (
    <>
      <Navbar />
      <EditorialPage>
        <StoryPageHero
          kicker="how it works"
          title={
            <>
              Five tabs.{" "}
              <Highlighter tone="gold">
                One feeling: I have somewhere to be.
              </Highlighter>
            </>
          }
          lede={
            <>
              Each tab removes one of the reasons spontaneous community
              doesn&rsquo;t happen anymore. None of it is paid.
            </>
          }
          marks={[
            {
              name: "squiggle",
              size: 80,
              rotate: 5,
              className: "right-[8%] top-[24%] text-teal/40",
            },
            {
              name: "starFilled",
              size: 24,
              className: "left-[6%] top-[26%] text-salmon/60",
            },
            {
              name: "loop",
              size: 58,
              rotate: -8,
              className: "right-[20%] bottom-[12%] text-ink/20",
            },
          ]}
        >
          <div className="flex flex-wrap gap-3">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/60 hover:text-salmon-dark border-b-2 border-ink/10 hover:border-salmon transition-colors pb-1"
              >
                {s.n} &middot; {s.title}
              </a>
            ))}
          </div>
        </StoryPageHero>

        {/* ── The 5 sections ────────────────────────────────────── */}
        {sections.map((s, i) => (
          <section
            key={s.id}
            id={s.id}
            // Grounds alternate so five near-identical rows read as five
            // chapters. The page was cream throughout, which is what made it
            // feel like documentation rather than a tour.
            className={cn(
              "relative overflow-hidden px-6 md:px-12 py-20 md:py-28 scroll-mt-24",
              [
                "bg-cream",
                "ground-butter",
                "bg-cream",
                "bg-paper-mint",
                "bg-cream",
              ][i],
            )}
          >
            <div className="max-w-6xl mx-auto">
              <div
                className={cn(
                  "grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center",
                  i % 2 === 1 && "md:[&>*:first-child]:order-2",
                )}
              >
                {/* Visual side */}
                <Reveal
                  y={30}
                  className={cn(
                    "md:col-span-5 transform-gpu transition-transform duration-300 hover:rotate-0",
                    i % 2 === 0 ? "-rotate-1" : "rotate-1",
                  )}
                >
                  {/* The real in-app mockups, the same ones the home page
                      Features section uses, rather than a lucide icon on a
                      tinted square. This page is a TOUR of five screens, so it
                      should show the screens. Each sits on the section's own
                      tinted ground (s.bg) so the five chapters stay visually
                      distinct the way the polaroids made them. */}
                  <div
                    className="rounded-3xl border border-ink/10 p-6 sm:p-8 shadow-[0_18px_44px_-20px_rgba(45,56,73,0.3)] flex items-center justify-center min-h-[380px] sm:min-h-[440px]"
                    style={{ backgroundColor: s.bg }}
                  >
                    <s.Visual />
                  </div>
                </Reveal>

                {/* Text side */}
                <Reveal y={30} delay={110} className="md:col-span-7">
                  <span className="font-headline text-6xl text-salmon/25 block mb-2 tabular-nums">
                    {s.n}
                  </span>
                  <p
                    className="text-2xl text-salmon-dark -rotate-1 mb-3"
                    style={handwritten}
                  >
                    {s.kicker}
                  </p>
                  <h2
                    className="font-headline text-ink text-4xl md:text-6xl tracking-[-0.025em] mb-6 leading-[1.1]"
                    style={{ fontWeight: 700 }}
                  >
                    {s.title}
                  </h2>
                  <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
                    {s.body}
                  </p>
                  <ul className="space-y-3">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-base text-gray-700"
                      >
                        <span className="text-salmon font-bold mt-0.5">·</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
            </div>
          </section>
        ))}

        {/* ── Footer CTA ───────────────────────────────────────── */}
        <section className="relative w-full ground-teal px-6 md:px-12 py-24 md:py-32 overflow-hidden">
          <Doodle
            name="burst"
            size={42}
            className="absolute w-8 h-8 md:w-auto md:h-auto left-[10%] top-[22%] text-salmon/65"
          />
          <Doodle
            name="arrow"
            size={64}
            rotate={-14}
            className="absolute w-8 h-8 md:w-auto md:h-auto right-[9%] bottom-[20%] text-salmon/50"
          />
          <Reveal y={28} className="relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-3xl text-salmon mb-3" style={handwritten}>
                ready?
              </p>
              <h3
                className="font-headline text-[#f9f9f7] text-4xl md:text-6xl tracking-[-0.03em] mb-6 leading-[1.12]"
                style={{ fontWeight: 700 }}
              >
                <Highlighter tone="coral">Loop in.</Highlighter>
              </h3>
              <p className="text-lg text-[#f9f9f7]/75 mb-8 max-w-md mx-auto">
                The app is free to join. iOS and Android. London first,
                expanding this year.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
                {/* Real store links (lib/appLinks.ts). These were
                    onClick={() => {}} no-ops from the story-site mock stage;
                    Button renders a <button> with no href support, so the
                    click opens the listing directly. */}
                <Button
                  variant="primary"
                  size="lg"
                  className="h-14 px-8"
                  onClick={() => {
                    trackStoreClick(APP_STORE_URL, "how-it-works");
                    window.open(APP_STORE_URL, "_blank", "noopener,noreferrer");
                  }}
                >
                  <AppleGlyph className="w-[18px] h-[18px] mr-2 inline-block align-[-3px]" />
                  App Store
                </Button>
                {/* Was variant="secondary", which does not exist: Button only
                    has primary/outline/ghost, so it fell through to no variant
                    classes at all and rendered as a bare label with no fill and
                    no border. The repo has no @types/react, so the union on
                    ButtonProps does not actually get enforced and tsc stayed
                    quiet. Outline, with the colours PINNED for this fixed teal
                    ground rather than the theme-flipping text-ink the variant
                    ships with. */}
                <Button
                  variant="outline"
                  size="lg"
                  className="h-14 px-8 border-2 border-[#f9f9f7]/45 text-[#f9f9f7] hover:bg-[#f9f9f7]/10"
                  onClick={() => {
                    trackStoreClick(PLAY_STORE_URL, "how-it-works");
                    window.open(
                      PLAY_STORE_URL,
                      "_blank",
                      "noopener,noreferrer",
                    );
                  }}
                >
                  <GooglePlayGlyph className="w-[18px] h-[18px] mr-2 inline-block align-[-3px]" />
                  Google Play
                </Button>
              </div>
              <Link
                to="/download"
                className="block mt-6 text-ink/70 font-semibold border-b-2 border-ink/20 hover:border-salmon hover:text-salmon transition-colors text-sm inline-flex items-center gap-1"
              >
                Or see what is on, in the app
                <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </section>
      </EditorialPage>
    </>
  );
}
